import React, { useState, useEffect, useMemo } from "react"
import _get from "lodash/get"
import _isEmpty from "lodash/isEmpty"
import _find from "lodash/find"
import _includes from "lodash/includes"
import _filter from "lodash/filter"
import _flatMap from "lodash/flatMap"
import { connect } from "react-redux"
import compose from "ramda/src/compose"
import PropTypes from "prop-types"
import classNames from "classnames"
import { translate } from "react-i18next"

import {
  getUsageProfilesConsolidated,
  downloadUsageProfilesFiles
} from "services/state/usage/action"
import { downloadFile } from "services/utils/downloadAudio"
import {
  TELEFONIA_FIXA,
  TELEFONIA_MOVEL
} from "services/state/billing/constants"

import DefaultContainer from "shared/components/DefaultContainer"
import DefaultTitle from "shared/components/DefaultTitle"
import Typograph from "../../components/Typograph"
import EmptyState from "shared/components/EmptyState/index.js"
import Chart from "./components/Chart"
import Loader from "./components/Loader"
import Button from "shared/components/Button/Button.js"
import SendModal from "./components/SendModal"
import ButtonLoading from "./components/ButtonLoading"
import { showToast } from "components/Toast"
import ProductForm from "./components/ProductForm"
import Link from "shared/components/Link"

import consumptionProfileEmpty from "assets/svg/icon-ok.svg"

import styles from "./styles.css"
import { T } from "ramda"

const ConsumptionProfile = ({
  products,
  customer,
  getUsageProfilesConsolidated,
  downloadUsageProfilesFiles,
  t
}) => {
  const [data, setData] = useState(null)
  const [sendModalIsOpen, setSendModalIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [currentAssetId, setCurrentAssetId] = useState(null)

  const productsData = _get(products, "data", {})

  const validProducts = useMemo(() => {
    const productsValid = _flatMap(
      _filter(productsData, (product) =>
        _includes(
          [TELEFONIA_FIXA, TELEFONIA_MOVEL],
          _get(product, "productLine.name", _get(product, "lineOfBusiness", ""))
        )
      ),
      "products"
    )

    return _filter(productsValid, (product) => {
      return product.status !== "CANCELADO" && product.status !== "BLOQUEADO"
    })
  }, [productsData])

  const productSelected = _find(validProducts, { assetId: currentAssetId })

  const showEmptyState = _isEmpty(validProducts)
  const hasValidProducts = !_isEmpty(validProducts)
  const showLoader = isInitialized && isLoading && hasValidProducts
  const showChart =
    isInitialized && !isLoading && !_isEmpty(data) && hasValidProducts
  const showChartEmpty = isInitialized && !isLoading && _isEmpty(data)

  const loadingData = () => {
    if (!productSelected) return

    setIsLoading(true)
    setIsInitialized(true)
    getUsageProfilesConsolidated({ productKey: productSelected.assetTag })
      .then((response) => setData(_get(response, "payload.data", [])))
      .finally(() => setIsLoading(false))
  }

  const handleOpenModal = () => setSendModalIsOpen(true)
  const handleCloseModal = () => setSendModalIsOpen(false)

  const handleDownload = () => {
    setIsDownloading(true)
    downloadUsageProfilesFiles(
      { serviceId: productSelected.assetTag },
      customer.data
    )
      .then((response) => {
        const url = _get(response, "payload.data.pathUrl", "")
        downloadFile(url, { target: "_blank" })
      })
      .catch(() => {
        showToast({
          message:
            "Cliente, ocorreu um erro ao tentar baixar histórico de consumo! Tente novamente dentro de alguns minutos.",
          type: "error"
        })
      })
      .finally(() => {
        setIsDownloading(false)
      })
  }

  useEffect(loadingData, [productSelected])

  return (
    <section className={styles.container}>
      <DefaultTitle>Perfil de consumo</DefaultTitle>

      {showEmptyState && (
        <EmptyState
          isContainer
          showTitle={false}
          icon={consumptionProfileEmpty}
          title="Você não possui dados de consumo disponíveis!"
          description="Atualmente, não encontramos informações sobre o seu perfil de consumo."
        >
          <Link className={styles.btnEmptyState} to="/">
            Ir para página inicial
          </Link>
        </EmptyState>
      )}

      {hasValidProducts && (
        <DefaultContainer>
          <Typograph
            htmlTag="p"
            size="medium"
            weight="regular"
            color="outer_space"
            classNames={classNames(styles.mb24, styles.textLeft)}
          >
            Para ver os dados de consumo, selecione o tipo do produto e a linha
            desejada, e os dados serão carregados abaixo
          </Typograph>

          <ProductForm
            validProducts={validProducts}
            onChange={(values) => setCurrentAssetId(values.assetId)}
          />
        </DefaultContainer>
      )}

      {showLoader && (
        <DefaultContainer>
          <Loader />
        </DefaultContainer>
      )}

      {showChart && (
        <DefaultContainer>
          <Chart profileData={data} />
        </DefaultContainer>
      )}

      {showChartEmpty && (
        <EmptyState
          isContainer
          showTitle={false}
          icon={consumptionProfileEmpty}
          title="Você não possui dados de consumo disponíveis!"
          description="Atualmente, não encontramos informações sobre o seu perfil de consumo."
        >
          <Link className={styles.btnEmptyState} to="/">
            Ir para página inicial
          </Link>
        </EmptyState>
      )}

      {showChart && (
        <div className={styles.containerBtn}>
          <Button
            buttonStyle="secondary"
            className={styles.btnSendEmail}
            onClick={handleOpenModal}
            disabled={isDownloading}
          >
            Enviar por e-mail
          </Button>
          <ButtonLoading
            className={styles.btnDownload}
            onClick={handleDownload}
            isLoading={isDownloading}
          >
            Fazer download
          </ButtonLoading>
        </div>
      )}

      <SendModal
        title={T("MSG_0045")}
        isOpen={sendModalIsOpen}
        onClose={handleCloseModal}
        productSelected={productSelected}
      />
    </section>
  )
}

ConsumptionProfile.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      assetId: PropTypes.string,
      assetTag: PropTypes.string,
      description: PropTypes.string
    })
  ),
  customer: PropTypes.shape({
    data: PropTypes.any
  }),
  getUsageProfilesConsolidated: PropTypes.func,
  downloadUsageProfilesFiles: PropTypes.func,
  t: PropTypes.func
}

ConsumptionProfile.defaultProps = {
  products: [],
  customer: { data: null },
  getUsageProfilesConsolidated: () => Promise.resolve(),
  downloadUsageProfilesFiles: () => Promise.resolve(),
  t: () => {}
}

const mapStateToProps = ({ products, customer }) => ({
  products,
  customer
})

const enhance = compose(
  translate(),
  connect(mapStateToProps, {
    getUsageProfilesConsolidated,
    downloadUsageProfilesFiles
  })
)

export default enhance(ConsumptionProfile)



// WEBPACK FOOTER //
// ./src/scenes/ConsumptionProfile/index.js